import { useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { Card, EmptyState } from '../components/ui'
import { useModuleEnabled } from '../navigation'
import { guideChapters } from './content'
import { rich } from './rich'
import { ROLE_AUDIENCE, ROLE_RANK, VIEWER_ROLES, canView, type GuideSection, type ViewerRole } from './types'
import { Num, Wireframe } from './Wireframe'
import './guide.css'

/**
 * Vejledningen (beslutning 089).
 *
 * Viser kun de afsnit, brugerens rolle kan bruge - rollerne er kumulative, så en
 * projektleder også ser medarbejderens - og kun moduler, der er slået til. Den, der
 * har en højere rolle, kan se vejledningen, som en kollega med en lavere rolle ser
 * den, men aldrig højere end sin egen.
 *
 * Det er en vejledning, ikke en adgangskontrol: serveren håndhæver rettighederne.
 */
/** Hvem man kan se vejledningen som, ud fra sin egen rolle. */
const VISIBLE_AS: Record<ViewerRole, ViewerRole[]> = {
  Lærling: ['Lærling'],
  Medarbejder: ['Lærling', 'Medarbejder'],
  Projektleder: ['Lærling', 'Medarbejder', 'Projektleder'],
  Kontor: ['Lærling', 'Medarbejder', 'Projektleder', 'Kontor'],
  Administrator: ['Lærling', 'Medarbejder', 'Projektleder', 'Kontor', 'Administrator'],
  Superadmin: VIEWER_ROLES
}

export function GuidePage() {
  const { isSuperadmin, isAdmin, isProjectManager, hasRole, canUseVpn } = useAuth()
  const moduleEnabled = useModuleEnabled()

  const ownRole: ViewerRole = isSuperadmin
    ? 'Superadmin'
    : isAdmin
      ? 'Administrator'
      : hasRole('Kontor')
        ? 'Kontor'
        : isProjectManager
          ? 'Projektleder'
          : canUseVpn
            ? 'Medarbejder'
            : 'Lærling'

  const [viewAs, setViewAs] = useState<ViewerRole>(ownRole)
  const [query, setQuery] = useState('')
  const [tocOpen, setTocOpen] = useState(() => {
    try {
      return window.matchMedia('(min-width: 901px)').matches
    } catch {
      return true
    }
  })

  // Man kan kun se vejledningen som en rolle på eller under sin egen.
  const selectableRoles = VIEWER_ROLES.filter((r) => VISIBLE_AS[ownRole].includes(r))
  const shownRole: ViewerRole = selectableRoles.includes(viewAs) ? viewAs : ownRole
  const q = query.trim().toLowerCase()

  const chapters = guideChapters
    .map((chapter) => ({
      ...chapter,
      sections: chapter.sections.filter(
        (s) => canView(s, shownRole) && moduleEnabled(s.module) && (!q || searchText(s).includes(q))
      )
    }))
    .filter((chapter) => chapter.sections.length > 0)

  const count = chapters.reduce((sum, chapter) => sum + chapter.sections.length, 0)

  return (
    <div className="stack" style={{ gap: 16 }}>
      <Card>
        <h2 style={{ fontSize: 22 }}>❓ Vejledning</h2>
        <p className="small muted" style={{ margin: '6px 0 12px' }}>
          Sådan bruger du systemet - trin for trin og med skitser af skærmene. Tallene i de orange cirkler
          passer til tallene i trinene. Du ser det, din rolle kan bruge, og ikke moduler, der er slået fra.
          Tekst med <strong>fed</strong> er det, der står på knapperne.
        </p>
        <div className="row" style={{ flexWrap: 'wrap', gap: 10 }}>
          <input
            type="search"
            style={{ maxWidth: 320 }}
            placeholder="Søg, f.eks. ferie, kode eller mærkat"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {selectableRoles.length > 1 ? (
            <label className="row small" style={{ gap: 6 }}>
              Vis som
              <select
                style={{ width: 'auto' }}
                value={shownRole}
                onChange={(e) => setViewAs(e.target.value as ViewerRole)}
              >
                {selectableRoles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <span className="guide-role">Din rolle: {ownRole}</span>
          )}
          <span className="tiny muted">{count} afsnit</span>
        </div>
        {shownRole !== ownRole && (
          <p className="tiny muted" style={{ margin: '8px 0 0' }}>
            Du ser vejledningen, som en {shownRole.toLowerCase()} ser den.
          </p>
        )}
      </Card>

      {count === 0 ? (
        <EmptyState>Intet i vejledningen passer på "{query}".</EmptyState>
      ) : (
        <div className="guide">
          <nav className="card guide__toc" aria-label="Indhold">
            <details open={tocOpen} onToggle={(e) => setTocOpen((e.target as HTMLDetailsElement).open)}>
              <summary>Indhold</summary>
              {chapters.map((chapter) => (
                <div key={chapter.id}>
                  <div className="guide__toc-chapter">
                    {chapter.icon} {chapter.title}
                  </div>
                  {chapter.sections.map((s) => (
                    <a key={s.id} href={`#g-${s.id}`}>
                      {s.icon} {s.title}
                    </a>
                  ))}
                </div>
              ))}
            </details>
          </nav>

          <div className="guide__main">
            {chapters.map((chapter) => (
              <div key={chapter.id} className="stack" style={{ gap: 16 }}>
                <div>
                  <h2 className="guide-chapter" id={`k-${chapter.id}`}>
                    {chapter.icon} {chapter.title}
                  </h2>
                  <p className="small muted" style={{ margin: '4px 0 0' }}>
                    {rich(chapter.intro)}
                  </p>
                </div>
                {chapter.sections.map((section) => (
                  <GuideSectionCard key={section.id} section={section} />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function GuideSectionCard({ section }: { section: GuideSection }) {
  const hasSketch = Boolean(section.sketch)
  const wide = hasSketch && section.sketch!.device !== 'phone'

  return (
    <section id={`g-${section.id}`} className="card guide-section">
      <header className="row row--between" style={{ alignItems: 'flex-start', gap: 10 }}>
        <h3 style={{ fontSize: 18 }}>
          {section.icon} {section.title}
        </h3>
        <span className={`guide-role guide-role--r${ROLE_RANK[section.role]}`}>{ROLE_AUDIENCE[section.role]}</span>
      </header>
      {section.where && (
        <div className="tiny muted" style={{ marginTop: 2 }}>
          Findes: {rich(section.where)}
        </div>
      )}
      <p className="small" style={{ margin: '8px 0 0' }}>
        {rich(section.intro)}
      </p>

      {(hasSketch || section.flows?.length) && (
        <div
          className={`guide-section__grid${wide ? ' guide-section__grid--wide' : ''}${hasSketch ? '' : ' guide-section__grid--nosketch'}`}
        >
          {section.sketch && <Wireframe sketch={section.sketch} />}
          <div className="stack" style={{ gap: 14 }}>
            {section.flows?.map((flow) => (
              <div key={flow.title}>
                <div className="guide-flow__title">{flow.title}</div>
                <ol className="guide-steps">
                  {flow.steps.map((step, i) => {
                    const s = typeof step === 'string' ? { text: step } : step
                    return (
                      <li key={i}>
                        <Num n={s.n} />
                        {rich(s.text)}
                      </li>
                    )
                  })}
                </ol>
              </div>
            ))}
          </div>
        </div>
      )}

      {section.tips && section.tips.length > 0 && (
        <div className="guide-tips small">
          <strong>Godt at vide</strong>
          <ul>
            {section.tips.map((tip, i) => (
              <li key={i}>{rich(tip)}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

function searchText(section: GuideSection): string {
  const steps = section.flows?.flatMap((f) => [f.title, ...f.steps.map((s) => (typeof s === 'string' ? s : s.text))]) ?? []
  return [section.title, section.where ?? '', section.intro, ...steps, ...(section.tips ?? [])].join(' ').toLowerCase()
}
